import { pick, prop } from 'ramda';
import {
    usePortletConfig,
    usePortletDispatch,
    usePortletSelector,
    usePortletTranslations,
    withPortlet
} from 'js/components/system/Portlet';
import React, { useMemo } from 'react';
import { usePushSubscription } from 'server';
import { useLiveGames } from 'js/hooks/games';
import { componentService } from 'js/services/component.service';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';

import Empty from './Empty';
import Filters from './Filters';
import Skeleton from './Skeleton';
import { Search } from './Search';
import { GAMES } from './constants';
import { Category } from './Category';
import { useTracking } from './trackingApi';
import { gamesPushAction } from './actions';
import { useLoadImages } from './loadImages';
import CategoriesTabs from './CategoriesTabs';
import reducer, { initialState } from './reducer';
import { useRecentlyPlayed } from './useRecentlyPlayed';
import { useFiltersEnabled } from './useFiltersEnabled';
import { useCategories, useFeed } from './getCategories';
import { useFetchCategoriesFromBI } from './BICategories';
import useFilteredCategories from './useFilteredCategories';

import style from './index.scss';

export const GamesBare = () => {
    useFeed();
    useCategories();
    const t = usePortletTranslations();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const {
        isDynamicCategoriesFetched,
        isImagesLoaded,
        recentlyPlayedIsLoading,
        liveLaunchAliases
    } = usePortletSelector(pick([
        'isDynamicCategoriesFetched',
        'isImagesLoaded',
        'recentlyPlayedIsLoading',
        'liveLaunchAliases'
    ]));
    const { showSearch: searchEnabled } = usePortletConfig();
    const filtersEnabled = useFiltersEnabled();
    const showHeader = searchEnabled || filtersEnabled;
    const dispatch = usePortletDispatch();

    const renderHeader = useMemo(() => {
        const elements = [];

        if (searchEnabled) {
            elements.push(<Search key={1} className={style.search} />);
        }

        if (filtersEnabled) {
            elements.push(<Filters key={2} className={style.filters} />);
        }

        if (isDesktop) {
            elements.reverse();
        }

        return (
            <div className={style.header}>
                {elements.map(item => item)}
            </div>
        );
    }, [filtersEnabled, searchEnabled, isDesktop]);

    useLoadImages();

    useFetchCategoriesFromBI();

    useRecentlyPlayed();

    const isFavoritesFetching = useAppSelector(prop('isFavoritesFetching'));
    const shouldRenderSkeleton = recentlyPlayedIsLoading
        || isFavoritesFetching
        || !isImagesLoaded
        || !isDynamicCategoriesFetched;

    usePushSubscription(31133, () => pushMessage => dispatch(gamesPushAction(pushMessage)));

    useFilteredCategories(filtersEnabled);

    useLiveGames({ liveLaunchAliases, preventSubscribe: shouldRenderSkeleton });

    useTracking();

    const { tabs, sliders, length: categoriesLength } = usePortletSelector(prop('groupedFilteredCategories'));

    if (shouldRenderSkeleton) {
        return <Skeleton />;
    }

    return (
        <div className={style.container}>
            {showHeader && renderHeader}
            {sliders.map(category => (
                <Category
                    key={category.cid}
                    {...category}
                />
            ))}
            {tabs.length > 0 && <CategoriesTabs categories={tabs} />}
            {categoriesLength === 0 && <Empty text={t('empty')} />}
        </div>
    );
};

export const Games = withPortlet({
    reducer,
    initialState,
    name: 'Games'
})(GamesBare);

componentService.registerPageComponents({ [GAMES]: Games });
